/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  decryptAES,
  maskEmailID,
  maskPhoneNumber,
  passkeyOtpGenerateRequest,
  passkeyOtpValidateRequest,
  resetPasskeyOtpGenerate,
  resetPasskeyOtpValidate,
  RootState,
  useRecaptchaToken,
} from '@rb-serve-app/core';
import { Xclose } from '../../assets/svg/SignInIcons';
import { BalanceIcon } from '../../assets/svg/StatusIcon';
import InlineError from '../../components/ErrorComponents/InlineError';

interface PasskeyVerificationCodeModalProps {
  selectedContact: string;
  preferenceType: string;
  userName: string;
  passkeyToken: string;
  onBack: () => void;
  onClose: () => void;
  onOtpSuccess: () => void;
}

const decryptValue = (value?: string): string => {
  if (!value) {
    return '';
  }
  try {
    return decryptAES(value) || value;
  } catch {
    return value;
  }
};

const PasskeyVerificationCodeModal = ({
  selectedContact,
  preferenceType,
  userName,
  passkeyToken,
  onBack,
  onClose,
  onOtpSuccess,
}: PasskeyVerificationCodeModalProps) => {
  const dispatch = useDispatch();
  const getRecaptchaToken = useRecaptchaToken();
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const hasTriggeredSuccess = useRef(false);

  const { contentfulData } = useSelector(
    (state: RootState) => state.contentful
  );
  const { entries } = contentfulData || {};
  const otpContentFullData = entries?.rb_otp_flow;
  const errorContent = otpContentFullData?.blockedUserPopupContent;

  const schema = Yup.object().shape({
    verificationCode: Yup.string()
      .matches(/^[0-9]+$/, errorContent?.err_num_only)
      .length(8, errorContent?.err_max_length),
  });

  const {
    register,
    handleSubmit,
    watch,
    resetField,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: { verificationCode: '' },
  });

  const selectedOtp = watch('verificationCode') || '';
  const [otp, setOtp] = useState('');
  const [isDisabled, setDisabled] = useState(true);
  const [istryAgain, setIstryAgain] = useState(false);
  const [isSuccessMsg, setIssuccessMsg] = useState(false);
  const [resendClick, setRendClick] = useState(false);
  const [error, setError] = useState<{
    title: string;
    description: string;
  } | null>(null);

  const { loading: isValidatePasscodeLoading, isValidated, error: validateError } =
    useSelector((state: RootState) => state.passkeyOtpValidate);
  const { loading: isGenerateCodeLoading, generated } = useSelector(
    (state: RootState) => state.passkeyOtpGenerate
  );

  useEffect(() => {
    setOtp(selectedOtp);
    if (selectedOtp?.length === 8 && !errors?.verificationCode?.message) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  }, [selectedOtp, errors?.verificationCode]);

  useEffect(() => {
    if (generated && resendClick) {
      setIssuccessMsg(true);
      dispatch(resetPasskeyOtpGenerate());
    }
  }, [generated, resendClick, dispatch]);

  useEffect(() => {
    if (validateError != null) {
      const status = validateError?.status ?? validateError?.statusCode;
      if (status === '404' || status === 404) {
        setIstryAgain(true);
        setError(null);
      } else if (status == '401') {
        setError({
          title:
            entries?.rb_error?.recaptchaErrors[validateError?.errorCode]?.title,
          description:
            entries?.rb_error?.recaptchaErrors[validateError?.errorCode]
              ?.description,
        });
      } else {
        setError({
          title: entries?.rb_error?.genericApiFailure?.text,
          description: entries?.rb_error?.genericApiFailure?.text,
        });
      }
    }
  }, [validateError, entries]);

  useEffect(() => {
    if (!isValidated || hasTriggeredSuccess.current) {
      return;
    }
    hasTriggeredSuccess.current = true;
    const timer = setTimeout(() => {
      onOtpSuccess();
    }, 2000);
    return () => clearTimeout(timer);
  }, [isValidated, onOtpSuccess]);

  const handelContinue = async () => {
    if (isDisabled) {
      return;
    }
    const recaptchaToken = await getRecaptchaToken('passkey_submit_otp');
    dispatch(
      passkeyOtpValidateRequest({
        oneTimePassCode: otp,
        username: userName,
        passkeyToken,
        recaptchaToken,
      })
    );
  };

  const resendCode = async () => {
    setRendClick(true);
    setIssuccessMsg(false);
    clearErrors();
    setIstryAgain(false);
    setError(null);
    dispatch(resetPasskeyOtpValidate());
    const recaptchaToken = await getRecaptchaToken('passkey_generate_otp');
    dispatch(
      passkeyOtpGenerateRequest({
        preferenceType,
        username: userName,
        passkeyToken,
        recaptchaToken,
      })
    );
  };

  const maskedContact =
    preferenceType === 'EMAIL'
      ? maskEmailID(decryptValue(selectedContact))
      : maskPhoneNumber(decryptValue(selectedContact));

  return (
    <div
      role="dialog"
      aria-labelledby="passkey-code-title"
      aria-describedby="passkey-code-description"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 min-h-screen"
    >
          <div
            className="xl:w-[45%] lg:w-[80%] md:w-[70%] sm:w-full md:max-h-[88vh] md:h-auto max-h-full h-full overflow-y-auto bg-white md:rounded-[15px] rounded-none shadow-lg md:p-8 p-[15px]"
            ref={modalRef}
            tabIndex={-1}
          >
            <div className="flex flex-col justify-center items-center relative bg-white">
              <button
                ref={closeButtonRef}
                className="absolute top-0 right-0 text-black hover:text-gray-700 focus:outline-none h-6 w-6"
                aria-label="Close modal"
                onClick={onClose}
              >
                <Xclose />
              </button>

              {isSuccessMsg && (
                <div className="mb-5 mt-8">
                  <InlineError
                    errorText={otpContentFullData?.successMsg}
                    onClose={() => setIssuccessMsg(false)}
                    fontSize={'15px'}
                    error={false}
                  />
                </div>
              )}
              {istryAgain && (
                <div className="mb-5 mt-8">
                  <InlineError
                    errorText={otpContentFullData?.invalidCodePopupContent}
                    onClose={() => setIstryAgain(false)}
                    fontSize={'15px'}
                    error={true}
                  />
                </div>
              )}
              {error && (
                <div className="mb-5 mt-8">
                  <InlineError
                    errorText={error?.description}
                    aria-live="assertive"
                    fontSize={'text-[15px]'}
                    onClose={() => setError(null)}
                    oncloseHide={true}
                  />
                </div>
              )}
              <h2
                id="passkey-code-title"
                className="xl:text-[28px] lg:text-[16px] xl:font-[400] font-[500] text-left md:text-center sm:mb-5 xl:mb-5 md:mb-0 lg:mb-0 mt-[10px] mb-5 self-baseline md:self-center"
              >
                {otpContentFullData?.verificationTitle}
              </h2>
              <p
                id="passkey-code-description"
                className="text-[15px] xl:text-[16px] text-left md:text-center font-[400] mb-[30px]"
              >
                {(otpContentFullData?.codeSentMsgWeb || '').replace(
                  '%s',
                  maskedContact
                )}
              </p>

              <form onSubmit={handleSubmit(handelContinue)} noValidate>
                <div className="mb-[30px] flex justify-center items-center flex-col">
                  <label
                    htmlFor="passkeyVerificationCode"
                    className="block text-[16px] font-[400] w-full md:w-[420px] self-center"
                  >
                    {otpContentFullData?.inputLabel}
                  </label>
                  <div className="relative w-full md:w-[420px]">
                    <input
                      type="text"
                      id="passkeyVerificationCode"
                      {...register('verificationCode')}
                      maxLength={8}
                      className={`h-[48px] md:max-w-[420px] max-w-[100%] py-[12px] px-[15px] mt-1 block w-full border border-light-gray-border shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm  ${
                        errors.verificationCode ? 'border-red-500' : ''
                      }`}
                      placeholder={otpContentFullData?.idEnterCodeTitle}
                      aria-invalid={errors.verificationCode ? 'true' : 'false'}
                    />
                    {errors.verificationCode && (
                      <div className="flex gap-1 items-center">
                        <div aria-label="balanceIcon">
                          <BalanceIcon fill="#DA291C" width="14" height="14" />
                        </div>
                        <p
                          className="text-red-600"
                          aria-label={errors.verificationCode.message}
                        >
                          {errors.verificationCode.message}
                        </p>
                      </div>
                    )}
                    <button
                      type="button"
                      className="absolute top-3 right-2 text-black hover:text-gray-700 focus:outline-none h-6 w-6"
                      aria-label="clear OTP"
                      onClick={() => {
                        resetField('verificationCode');
                        setOtp('');
                      }}
                    >
                      {otp && <Xclose />}
                    </button>
                  </div>
                </div>
                <p
                  className="text-[16px] text-center font-[400] p-6 mb-[30px] bg-[#FAFAFA]"
                  aria-label={errorContent?.verification_code_content}
                >
                  {errorContent?.verification_code_content}
                </p>
                <p className="text-[16px] text-center font-[400] mb-[30px]">
                  {otpContentFullData?.byContinuingAgreeTitle}{' '}
                  <a
                    href={errorContent?.otp_terms_condition_url}
                    className="text-primary-red hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={otpContentFullData?.termsConditions}
                  >
                    {otpContentFullData?.termsConditions}
                  </a>{' '}
                  {otpContentFullData?.termsConditionsEndTitle}
                </p>
                <div
                  className="flex justify-center items-center gap-[20px] mt-[30px] border-b border-light-gray-border"
                  style={{ marginBottom: '30px', paddingBottom: '30px' }}
                >
                  <button
                    aria-label={
                      otpContentFullData?.idAccountLockedCtaSecondaryTitle
                    }
                    type="button"
                    onClick={onClose}
                    className="p-[12px] text-text16 font-[500] bg-white hover:bg-red-50 text-primary-red focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50 border border-primary-red w-[160px] h-[48px]"
                  >
                    {otpContentFullData?.idAccountLockedCtaSecondaryTitle}
                  </button>
                  <div className="relative w-[160px]">
                    <button
                      aria-label={otpContentFullData?.continueButtonTitle}
                      type="submit"
                      disabled={isDisabled || isValidatePasscodeLoading}
                      className={`${isDisabled && !isValidatePasscodeLoading ? 'bg-disabled-bg-color text-Text-disabled-btn border-[#D9D9D9] border min-h-[48px]' : 'bg-red-600 hover:bg-red-700 text-white focus:ring-red-500'} min-h-[48px] w-[160px]  font-medium py-2 px-4 focus:outline-none focus:ring-2 focus:ring-opacity-50 h-10`}
                    >
                      {isValidatePasscodeLoading
                        ? ''
                        : otpContentFullData?.continueButtonTitle}
                    </button>
                    {isValidatePasscodeLoading && (
                      <div className="flex justify-center items-center absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                        <div className="animate-spin ease-linear rounded-full border-4 border-t-4 border-white h-6 w-6"></div>
                      </div>
                    )}
                  </div>
                </div>
              </form>
              <p className="text-[20px] font-[400] leading-[25px]">
                {otpContentFullData?.changeContactMethod}
              </p>
              <div className="text-center">
                <p className="text-[16px] font-[400] mb-[30px]">
                  {otpContentFullData?.sendNewCodeTitle}{' '}
                  <span
                    aria-label={otpContentFullData?.resendCodeCtaTitle}
                    tabIndex={0}
                    className="text-primary-red font-bold hover:underline cursor-pointer block md:inline"
                    onClick={() => resendCode()}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        resendCode();
                      }
                    }}
                  >
                    {isGenerateCodeLoading
                      ? ''
                      : otpContentFullData?.resendCodeCtaTitle}
                  </span>
                </p>
                <p
                  className="block mt-2 text-sm text-primary-red hover:underline cursor-pointer pb-5"
                  onClick={onBack}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onBack();
                    }
                  }}
                  tabIndex={0}
                >
                  {otpContentFullData?.clickToChangeTitle}
                </p>
              </div>
            </div>
          </div>
    </div>
  );
};

export default PasskeyVerificationCodeModal;
